import { saveAs } from "file-saver";
import moment from "moment";

// Same palette/font as exportInvoiceOrdersExcel.js:
// header = solid start colour of COLOR_APP (#1A66BE), title = COLOR_TEXT (#5A54A2),
// font = "BoonBaan" (the real family name of FontLao2 / BB-Font.ttf used across the app).
const HEADER_FILL = "FF1A66BE";
const HEADER_FONT_COLOR = "FFFFFFFF";
const TITLE_FONT_COLOR = "FF5A54A2";
const STRIPE_FILL = "FFF2F2F2";
const BORDER_COLOR = "FFD9D9D9";
const FONT_NAME = "BoonBaan";

//* Must match LOW_STOCK_THRESHOLD in Products.js
const LOW_STOCK_THRESHOLD = 5;

const COLUMNS = [
  { header: "ລຳດັບ", key: "no", width: 8, align: "center" },
  { header: "ຊື່ສິນຄ້າ", key: "name", width: 40 },
  { header: "ລະຫັດ CF", key: "cfMessage", width: 16 },
  { header: "ຈຳນວນທີ່ນຳເຂົ້າ", key: "amountAll", width: 14, numFmt: "#,##0" },
  { header: "ຈຳນວນທີ່ຍັງເຫຼືອ", key: "amount", width: 14, numFmt: "#,##0" },
  { header: "ຈຳນວນທີ່ຂາຍໄປ", key: "amountSale", width: 14, numFmt: "#,##0" },
  { header: "ສະຖານະສະຕ໊ອກ", key: "stockStatus", width: 14, align: "center" },
  { header: "ປະເພດ", key: "category", width: 18 },
  { header: "ລາຄາຂາຍ", key: "price", width: 14, numFmt: "#,##0" },
  { header: "ລາຄາຕົ້ນທືນ", key: "originPrice", width: 14, numFmt: "#,##0" },
  { header: "ສະກຸນເງິນ", key: "currency", width: 10, align: "center" },
  { header: "ສ່ວນຫຼຸດ", key: "reduction", width: 14 },
  { header: "ວັນທີ່ສ້າງສິນຄ້າ", key: "createdAt", width: 18 },
  { header: "ໃຊ້ເຊວເພຈ", key: "salePage", width: 12, align: "center" },
];

const stockStatusOf = (amount) => {
  const qty = Number(amount) || 0;
  if (qty <= 0) return "ໝົດ";
  if (qty <= LOW_STOCK_THRESHOLD) return "ໃກ້ໝົດ";
  return "ປົກກະຕິ";
};

// Same rule as the table: no reductionType = percent (old data)
const reductionOf = (product) => {
  if (!(product?.reduction > 0)) return "-";
  if (product?.reductionType === "AMOUNT")
    return `${new Intl.NumberFormat("en-CA").format(product.reduction)} ${product?.currency ?? ""}`.trim();
  return `${product.reduction}%`;
};

const nameOf = (product) => {
  const options = (product?.optionValues || [])
    .map((option) => option?.value)
    .filter(Boolean)
    .join(" ");
  return `${product?.name || "ຍັງບໍ່ກໍານົດ"}${options ? ` ${options}` : ""}`;
};

const toRow = (product, index) => ({
  no: index + 1,
  name: nameOf(product),
  cfMessage: product?.cfMessage ?? "-",
  amountAll: Number(product?.amountAll) || 0,
  amount: Number(product?.amount) || 0,
  amountSale: Number(product?.amountSale) || 0,
  stockStatus: stockStatusOf(product?.amount),
  category: product?.categoryName || product?.category?.name || "-",
  price: Number(product?.price) || 0,
  originPrice: Number(product?.originPrice) || 0,
  currency: product?.currency ?? "",
  reduction: reductionOf(product),
  createdAt: product?.createdAt ? moment(product.createdAt).format("DD/MM/YYYY HH:mm") : "-",
  salePage: product?.isUsingSalePage === false ? "ປີດ" : "ເປີດ",
});

// Same colours as StockBadge / the sale-page column in Products.js
const STATUS_FONT_COLOR = {
  ໝົດ: "FFDC3545",
  ໃກ້ໝົດ: "FFB8860B",
  ເປີດ: "FF198754",
  ປີດ: "FFDC3545",
};

/**
 * Build and download a product list as .xlsx.
 *
 * exceljs is loaded on click only, so the ~1MB library never lands in the main bundle.
 */
export const exportProductsExcel = async ({ products, title, fileName, startIndex = 0 }) => {
  const exceljsModule = await import("exceljs");
  const ExcelJS = exceljsModule.default || exceljsModule;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Products", {
    views: [{ state: "frozen", ySplit: 2 }],
  });

  const lastColumn = COLUMNS.length;
  const baseFont = { name: FONT_NAME, size: 11 };
  const thinBorder = {
    top: { style: "thin", color: { argb: BORDER_COLOR } },
    left: { style: "thin", color: { argb: BORDER_COLOR } },
    bottom: { style: "thin", color: { argb: BORDER_COLOR } },
    right: { style: "thin", color: { argb: BORDER_COLOR } },
  };

  // Row 1: title across every column
  sheet.mergeCells(1, 1, 1, lastColumn);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { ...baseFont, size: 14, bold: true, color: { argb: TITLE_FONT_COLOR } };
  titleCell.alignment = { vertical: "middle", horizontal: "left" };
  sheet.getRow(1).height = 26;

  // Row 2: header in the app's colour
  const headerRow = sheet.getRow(2);
  COLUMNS.forEach((column, index) => {
    const cell = headerRow.getCell(index + 1);
    cell.value = column.header;
    cell.font = { ...baseFont, bold: true, color: { argb: HEADER_FONT_COLOR } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: HEADER_FILL } };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = thinBorder;
    sheet.getColumn(index + 1).width = column.width;
  });
  headerRow.height = 24;

  // Data rows (ລຳດັບ continues from the page offset, like the table)
  const rows = (products || []).map((product, index) => toRow(product, startIndex + index));
  rows.forEach((row, rowIndex) => {
    const excelRow = sheet.getRow(rowIndex + 3);
    COLUMNS.forEach((column, index) => {
      const cell = excelRow.getCell(index + 1);
      const value = row[column.key];
      cell.value = value;
      cell.font = STATUS_FONT_COLOR[value]
        ? { ...baseFont, bold: true, color: { argb: STATUS_FONT_COLOR[value] } }
        : baseFont;
      cell.border = thinBorder;
      cell.alignment = { vertical: "middle", horizontal: column.align, wrapText: column.key === "name" };
      if (column.numFmt) cell.numFmt = column.numFmt;
      if (rowIndex % 2 === 1)
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: STRIPE_FILL } };
    });
  });

  // Total row (quantities only — prices mix currencies)
  const totalRow = sheet.getRow(rows.length + 3);
  const sum = (key) => rows.reduce((acc, row) => acc + (Number(row[key]) || 0), 0);
  const totals = {
    name: `ລວມ ${rows.length} ລາຍການ`,
    amountAll: sum("amountAll"),
    amount: sum("amount"),
    amountSale: sum("amountSale"),
  };
  COLUMNS.forEach((column, index) => {
    const cell = totalRow.getCell(index + 1);
    cell.value = totals[column.key] ?? "";
    cell.font = { ...baseFont, bold: true, color: { argb: TITLE_FONT_COLOR } };
    cell.border = thinBorder;
    if (column.numFmt && totals[column.key] !== undefined) cell.numFmt = column.numFmt;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fileName);
  return rows.length;
};

// Same headers/order as the import template (DownloadExcelButton.js) — ProductExcelAdd.js
// reads columns by index and skips row 1, so no title row here.
const EDIT_COLUMNS = [
  { header: "ລະຫັດ CF", key: "cfMessage", width: 14 },
  { header: "ຊື່ສິນຄ້າ", key: "name", width: 36 },
  { header: "ຫົວຫນວ່ຍ", key: "unit", width: 12 },
  { header: "ຈໍານວນ", key: "amount", width: 10 },
  { header: "ລາຄາຕົ້ນທືນ", key: "originPrice", width: 14 },
  { header: "ລາຄາຂາຍ", key: "price", width: 14 },
  { header: "ສະກຸນເງິນ", key: "currency", width: 12 },
];

// The template uses Lao currency names (see ProductExcelAdd.js currency buttons)
const LAO_CURRENCY = { LAK: "ກີບ", BAHT: "ບາດ", USD: "ໂດລາ" };

/**
 * Download products in the "ເພີ່ມສິນຄ້າດ້ວຍ Excel" template format, for editing.
 * Prices are plain numbers (no thousand separators) as the import page requires.
 */
export const exportProductsForEditExcel = async ({ products, fileName }) => {
  const exceljsModule = await import("exceljs");
  const ExcelJS = exceljsModule.default || exceljsModule;

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Sheet1");
  const baseFont = { name: FONT_NAME, size: 11 };

  sheet.columns = EDIT_COLUMNS.map(({ key, width }) => ({ key, width }));
  sheet.addRow(EDIT_COLUMNS.map((column) => column.header));
  (products || []).forEach((product) => {
    sheet.addRow({
      cfMessage: product?.cfMessage ?? "",
      name: product?.name ?? "",
      unit: product?.unit ?? "",
      amount: Number(product?.amount) || 0,
      originPrice: Number(product?.originPrice) || 0,
      price: Number(product?.price) || 0,
      currency: LAO_CURRENCY[product?.currency] || product?.currency || "ກີບ",
    });
  });
  sheet.eachRow((row) => {
    row.eachCell({ includeEmpty: true }, (cell) => {
      cell.font = baseFont;
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  saveAs(blob, fileName);
  return products?.length || 0;
};
